// Memecoin presale draws: some NPC launches open a one-time lottery before they go live.
// Enter once (costs a little energy). If you're lucky you get a free bag at launch price;
// if not, nothing. The coin's hidden fate still applies: a lucky bag can still rug.

import { TUNABLES } from "@degen/shared/config/tunables";
import type { Rng } from "../rng";
import { pushLog, type GameState } from "../state";
import { playerClout } from "../player";
import { balanceOf, mintTo, priceOf, transferTokens, type Token } from "./token";

const PS = TUNABLES.presale;

export interface Presale {
  id: string;
  tick: number; // launch tick
  name: string;
  ticker: string;
  entrants: number; // flavor: how crowded the draw is
  entered: boolean;
  resolved: boolean;
  won?: boolean;
  tokenId?: string;
  bagUsd: number;
}

export function createPresale(state: GameState, rng: Rng, tick: number, name: string, ticker: string): Presale {
  const p: Presale = {
    id: `ps${tick}-${ticker}`,
    tick,
    name,
    ticker,
    entrants: rng.int(300, 9_000),
    entered: false,
    resolved: false,
    bagUsd: Math.round(rng.range(PS.bagUsd[0], PS.bagUsd[1])),
  };
  state.market.presales.push(p);
  if (state.market.presales.length > 24) state.market.presales.splice(0, state.market.presales.length - 24);
  pushLog(state, "presale_open", `🎟️ $${ticker} presale draw is open. Launches soon. Enter for a shot at a free bag.`, { presale: p.id });
  return p;
}

export function openPresales(state: GameState): Presale[] {
  return state.market.presales.filter((p) => !p.resolved && p.tick > state.tick);
}

export type PresaleResult = { ok: true } | { ok: false; error: string };

export function enterPresale(state: GameState, id: string): PresaleResult {
  const p = state.market.presales.find((x) => x.id === id);
  if (!p || p.resolved || p.tick <= state.tick) return { ok: false, error: "That draw is closed." };
  if (p.entered) return { ok: false, error: "You're already in. Fingers crossed." };
  p.entered = true;
  p.entrants++;
  return { ok: true };
}

/** Player's win chance for draws, from reach (bigger accounts get whitelisted more). */
export function presaleWinChance(state: GameState): number {
  const c = playerClout(state.player);
  const reach = c / (c + 5_000);
  return PS.winChance[0] + (PS.winChance[1] - PS.winChance[0]) * reach;
}

/** Called right after the presale's token spawns: draw the winners. */
export function resolvePresale(state: GameState, rng: Rng, p: Presale, t: Token) {
  p.resolved = true;
  p.tokenId = t.id;
  if (!p.entered) return;
  p.won = rng.chance(presaleWinChance(state));
  if (!p.won) {
    pushLog(state, "presale_lost", `🎟️ $${t.ticker} presale: not selected this time. ${p.entrants.toLocaleString()} people entered.`, { tokenId: t.id });
    return;
  }
  const tokens = p.bagUsd / priceOf(t);
  // The allocation comes out of the dev's bag where possible (that's how presales work).
  const fromDev = Math.min(tokens, balanceOf(t, t.devId) * 0.5);
  transferTokens(t, t.devId, "player", fromDev);
  mintTo(t, "player", tokens - fromDev);
  state.player.holdings.push({ kind: "token", tokenId: t.id, costUsd: 0 });
  pushLog(state, "presale_won", `🎉 You won the $${t.ticker} presale! Free bag worth ~$${p.bagUsd.toLocaleString()}. Will it run or rug?`, { tokenId: t.id });
}
